import { editorTextBoxDocumentToInput } from '@messenger/lotus'
import type { EditorTextBoxDocument } from '@messenger/lotus'
import type { EditorTextBoxInput } from '@messenger/lotus/row-store'
import { editorAnchorPageId } from './editorTextBoxSave'
import { sameEditorTextBoxContent } from './editorTextBoxItems'

/** What one page's rows take from the editor's pending work. */
export type EditorTextBoxPageWrites = {
  /** The drafts to write, each with the `expectedRevision` it was edited from. */
  boxes: EditorTextBoxInput[]
  /** The ids of those drafts, in the same order. */
  draftBoxIds: string[]
  /** The boxes pending deletion. */
  deleteBoxIds: string[]
}

const pageWrites = (
  byPage: Map<string, EditorTextBoxPageWrites>,
  pageId: string,
): EditorTextBoxPageWrites => {
  const existing = byPage.get(pageId)
  if (existing !== undefined) return existing
  const fresh: EditorTextBoxPageWrites = {
    boxes: [],
    draftBoxIds: [],
    deleteBoxIds: [],
  }
  byPage.set(pageId, fresh)
  return fresh
}

/**
 * The editor's pending text-box work, grouped by the page whose rows it
 * writes: every draft not pending deletion, unless `lastSaved` holds the same
 * content for it, and every pending delete. A draft whose anchor names no
 * page is left out. Pages come in the order their first draft, then their
 * first delete, names them.
 */
export const editorTextBoxPendingWrites = ({
  drafts,
  pendingDeletes,
  lastSaved,
}: {
  drafts: Readonly<Record<string, EditorTextBoxDocument>>
  /** Box id to the page it is deleted from. */
  pendingDeletes: Readonly<Record<string, string>>
  /** What each box last saved, when the caller keeps that record. */
  lastSaved?: ReadonlyMap<string, EditorTextBoxDocument>
}): Map<string, EditorTextBoxPageWrites> => {
  const byPage = new Map<string, EditorTextBoxPageWrites>()
  for (const [boxId, draft] of Object.entries(drafts)) {
    if (boxId in pendingDeletes) continue
    const pageId = editorAnchorPageId(draft.anchor)
    if (!pageId) continue
    const saved = lastSaved?.get(boxId)
    if (saved !== undefined && sameEditorTextBoxContent(saved, draft)) continue
    const writes = pageWrites(byPage, pageId)
    writes.boxes.push(editorTextBoxDocumentToInput(draft))
    writes.draftBoxIds.push(boxId)
  }
  for (const [boxId, pageId] of Object.entries(pendingDeletes))
    pageWrites(byPage, pageId).deleteBoxIds.push(boxId)
  return byPage
}
